import { useCallback, useEffect, useRef, useState } from 'react'

import userApi from '../../../services/api/user'

// Retry helper function with exponential backoff
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      const isNetworkError =
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Network request failed') ||
        error.name === 'TypeError' ||
        error.name === 'NetworkError'

      // Check if it's a 500 error (server error - might be transient)
      const isServerError = error.status === 500 || error.response?.status === 500

      // Retry on network errors or 500 server errors (but only once for 500s)
      const shouldRetry = isNetworkError || (isServerError && attempt < 1)
      
      if (!shouldRetry || attempt === maxRetries - 1) {
        throw error
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

const useNote = (subsidiaryId, kind = 'healthCheck-summary', { lazy = false, batchLoad = false } = {}) => {
  const [note, setNoteState] = useState('')
  const [isLoading, setIsLoading] = useState(!lazy && !batchLoad) // Don't load if lazy or batchLoad
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [hasLoaded, setHasLoaded] = useState(false) // Track if we've loaded
  const debounceTimerRef = useRef(null)
  const previousNoteRef = useRef('') // Store previous note to revert on error

  const key = `${kind}-${subsidiaryId}`
  const localStorageKey = `health-check-note:${key}`

  // Helper function to save to localStorage
  const saveToLocalStorage = useCallback((noteValue) => {
    if (!subsidiaryId) return
    try {
      if (noteValue && noteValue.trim()) {
        localStorage.setItem(localStorageKey, noteValue)
      } else {
        // Remove from localStorage if empty
        localStorage.removeItem(localStorageKey)
      }
    } catch (err) {
      console.warn('Failed to save note to localStorage:', err)
    }
  }, [localStorageKey, subsidiaryId])

  // Helper function to load from localStorage
  const loadFromLocalStorage = useCallback(() => {
    if (!subsidiaryId) return null
    try {
      const saved = localStorage.getItem(localStorageKey)
      return saved || null
    } catch (err) {
      console.warn('Failed to load note from localStorage:', err)
      return null
    }
  }, [localStorageKey, subsidiaryId])

  // Load note function
  const loadNote = useCallback(async () => {
    if (!subsidiaryId) {
      setIsLoading(false)
      return
    }

    if (hasLoaded) {
      return // Already loaded
    }

    // First, load from localStorage for immediate display
    const localNote = loadFromLocalStorage()
    if (localNote) {
      setNoteState(localNote)
      previousNoteRef.current = localNote
    }

    try {
      setIsLoading(true)
      setError(null)
      const response = await retryWithBackoff(() =>
        userApi.retrieveNote({
          key: key,
          kind: kind,
          subsidiaryId: subsidiaryId
        })
      )
      // Server data takes precedence over localStorage
      if (response?.data) {
        setNoteState(response.data)
        previousNoteRef.current = response.data
        // Update localStorage with server data
        saveToLocalStorage(response.data)
      } else {
        // If server has no data, keep localStorage value if it exists
        if (!localNote) {
          setNoteState('')
          previousNoteRef.current = ''
        }
      }
      setHasLoaded(true)
    } catch (err) {
      console.error('Failed to load note after retries:', err)
      // On error, keep localStorage value if available
      if (!localNote) {
        setNoteState('')
        previousNoteRef.current = ''
      }
      // Handle network errors more gracefully
      if (
        err.message?.includes('Failed to fetch') ||
        err.message?.includes('NetworkError') ||
        err.message?.includes('Network request failed')
      ) {
        setError('Unable to connect to server. Please check your connection.')
      } else {
        setError(err.message || 'Failed to load note')
      }
      // Still mark as loaded so we can use localStorage value
      setHasLoaded(true)
    } finally {
      setIsLoading(false)
    }
  }, [subsidiaryId, key, kind, hasLoaded, loadFromLocalStorage, saveToLocalStorage])

  // Load from localStorage on mount for immediate display (especially for batchLoad)
  useEffect(() => {
    if (!subsidiaryId) return
    
    const localNote = loadFromLocalStorage()
    if (localNote && !hasLoaded) {
      // Set from localStorage immediately for instant display
      setNoteState(localNote)
      previousNoteRef.current = localNote
    }
  }, [subsidiaryId, loadFromLocalStorage, hasLoaded])

  // Load note on mount (only if not lazy and not batchLoad)
  useEffect(() => {
    if (lazy || batchLoad) {
      setIsLoading(false)
      return
    }
    loadNote()
  }, [lazy, batchLoad, loadNote])

  // Function to load note on demand (for manual trigger or batch loading)
  const loadNoteOnDemand = useCallback(async () => {
    if (hasLoaded || isLoading) return
    await loadNote()
  }, [hasLoaded, isLoading, loadNote])

  // Expose loadNote for batch loading
  const loadNoteForBatch = useCallback(async () => {
    await loadNote()
  }, [loadNote])

  // Function to set note from external source (e.g., batch loading)
  const setNoteFromBatch = useCallback((newNote) => {
    const noteValue = newNote || ''
    setNoteState(noteValue)
    previousNoteRef.current = noteValue
    // Update localStorage when batch loading
    saveToLocalStorage(noteValue)
    setHasLoaded(true)
    setIsLoading(false)
  }, [saveToLocalStorage])

  // Internal save function (used by both debounced and immediate saves)
  const performSave = useCallback(
    async (newNote, previousNote) => {
      if (!subsidiaryId) return

      // Save to localStorage immediately (synchronous)
      saveToLocalStorage(newNote)

      try {
        setIsSaving(true)
        setError(null)
        
        // Validate required parameters
        if (!subsidiaryId) {
          throw new Error('SubsidiaryId is required to save note')
        }
        
        await retryWithBackoff(() =>
          userApi.addOrUpdateNote({
            key: key,
            kind: kind,
            data: newNote,
            subsidiaryId: subsidiaryId
          })
        )
        
        // Update previous note ref on success
        previousNoteRef.current = newNote
        // localStorage already updated above, so we're good
        setIsSaving(false)
        return true
      } catch (err) {
        console.error('Failed to save note after retries:', err)
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          key,
          kind,
          subsidiaryId
        })
        
        // On error, keep the localStorage value (it's already saved)
        // Only revert UI state if it's a critical error
        // For network errors, keep the optimistic update since localStorage has it
        const isNetworkError = 
          err.message?.includes('Failed to fetch') ||
          err.message?.includes('NetworkError') ||
          err.message?.includes('Network request failed') ||
          err.message?.includes('CORS') ||
          err.toString().includes('CORS')

        if (!isNetworkError) {
          // For non-network errors, revert to previous value
          setNoteState(previousNote)
          previousNoteRef.current = previousNote
          // Remove from localStorage on non-network error
          saveToLocalStorage(previousNote)
        }
        // For network errors, keep the optimistic update (localStorage already has it)

        // Handle network errors more gracefully
        if (isNetworkError) {
          setError('Unable to connect to server. Note saved locally and will sync when connection is restored.')
        } else {
          setError(err.message || 'Failed to save note')
        }
        setIsSaving(false)
        throw err
      }
    },
    [key, kind, subsidiaryId, saveToLocalStorage]
  )

  // Save note with debounce
  const saveNote = useCallback(
    async newNote => {
      if (!subsidiaryId) return

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Store previous value from ref before optimistic update
      const previousNote = previousNoteRef.current

      // Optimistically update UI
      setNoteState(newNote)
      setError(null)
      
      // Save to localStorage immediately (synchronous)
      saveToLocalStorage(newNote)

      // Debounce the API call (wait 500ms after last change)
      return new Promise((resolve, reject) => {
        debounceTimerRef.current = setTimeout(async () => {
          try {
            await performSave(newNote, previousNote)
            resolve()
          } catch (err) {
            reject(err)
          }
        }, 500)
      })
    },
    [subsidiaryId, performSave, saveToLocalStorage]
  )

  // Save immediately (for onBlur, etc.)
  const saveImmediately = useCallback(
    async (newNote) => {
      if (!subsidiaryId) return

      // Clear any pending debounced save
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }

      // Store previous value from ref before optimistic update
      const previousNote = previousNoteRef.current

      // Optimistically update UI
      setNoteState(newNote)
      setError(null)

      // Save immediately
      try {
        await performSave(newNote, previousNote)
      } catch (err) {
        // Error already handled in performSave
      }
    },
    [subsidiaryId, performSave]
  )

  return {
    note,
    setNote: saveNote,
    saveImmediately, // Expose for immediate saves (e.g., onBlur)
    isLoading,
    isSaving,
    error,
    loadNoteOnDemand, // Expose for on-demand loading
    loadNoteForBatch, // Expose for batch loading
    setNoteFromBatch // Expose for batch-loaded notes
  }
}

export default useNote
